import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Loader2, IndianRupee, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	intelligenceApi,
	type CommandCentreKpi,
	type IntelligencePeriodPreset,
} from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
	CommandCentreFilters,
	type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { KpiCard } from "@/components/intelligence/KpiCard";
import {
	FinanceMetricCard,
	UnavailableFinanceCard,
} from "@/components/intelligence/FinanceMetricCard";
import { FinanceTrendChart } from "@/components/intelligence/FinanceTrendChart";
import { RevenueCollectionMomPanel } from "@/components/intelligence/RevenueCollectionMomPanel";
import { FinanceHospitalComparisonTable } from "@/components/intelligence/FinanceHospitalComparisonTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatInr } from "@/lib/intelligenceFormat";
import { IntelligenceExportButton } from "@/components/intelligence/IntelligenceExportButton";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { buildFinanceExportRows } from "@/lib/intelligenceExport";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend
);

const CORE_KPI_KEYS = ["gross_billing", "net_revenue", "collections", "outstanding"] as const;

const FINANCE_METRIC_KEYS = [
	"refunds",
	"discounts",
	"collection_efficiency",
	"operating_expense",
	"capex_expense",
	"funding_expense",
	"operating_profit",
	"operating_margin",
	"cash_inflow",
	"cash_outflow",
	"net_cash_flow",
] as const;

function toCommandCentreKpi(k: {
	key: string;
	name: string;
	domain: string;
	unit: string;
	value: number;
	previousValue: number | null;
	absoluteChange: number | null;
	percentChange: number | null;
	trend: "up" | "down" | "flat" | null;
	dataAvailability: CommandCentreKpi["dataAvailability"];
	limitations: string | null;
	definition: { supportsComparison: boolean };
	period: { to: string };
}): CommandCentreKpi {
	return {
		key: k.key,
		displayName: k.name,
		name: k.name,
		domain: k.domain,
		unit: k.unit,
		value: k.value,
		previousValue: k.previousValue,
		absoluteChange: k.absoluteChange,
		percentChange: k.percentChange,
		trend: k.trend,
		dataAvailability: k.dataAvailability,
		limitations: k.limitations,
		snapshotAsOf: k.key === "outstanding" ? k.period.to.slice(0, 10) : null,
		supportsComparison: k.definition.supportsComparison,
	};
}

export default function FinanceIntelligencePage() {
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isInvestor = user?.role === "INVESTOR";
	const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
	const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
	const chainHospitals = user?.hospitals ?? [];
	const isChainScoped = isChainAdmin || investorHasChainScope;

	const defaultFinancialHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({ preset: "this_month" });
	const [selectedHospitalId, setSelectedHospitalId] = useState("");

	useEffect(() => {
		if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
			setSelectedHospitalId(defaultFinancialHospitalId || "all");
		}
	}, [defaultFinancialHospitalId, selectedHospitalId]);

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-finance"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return superHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (isChainScoped && chainHospitals.length) {
			return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (user?.hospitalId) {
			const name = chainHospitals.find((h) => h.id === user.hospitalId)?.name ?? "My Hospital";
			return [{ id: user.hospitalId, name }];
		}
		return [];
	}, [isSuperAdmin, isChainScoped, superHospitals, chainHospitals, user?.hospitalId]);

	const reportHospitalId = useMemo(
		() =>
			intelligenceReportHospitalId({
				showHospitalFilter,
				selectedHospitalId,
				isChainScoped,
			}),
		[showHospitalFilter, selectedHospitalId, isChainScoped]
	);

	const customReady =
		period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

	const queryParams = {
		period: period.preset as IntelligencePeriodPreset,
		from: period.preset === "custom" ? period.from : undefined,
		to: period.preset === "custom" ? period.to : undefined,
		hospitalId: reportHospitalId,
		compare: true,
	};

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["intelligence-finance", queryParams],
		queryFn: () => intelligenceApi.getFinance(queryParams),
		enabled: customReady,
	});

	const coreKpis = useMemo(() => {
		if (!data) return [];
		return CORE_KPI_KEYS.map((key) => data.coreKpis.find((k) => k.key === key))
			.filter((k): k is NonNullable<typeof k> => !!k)
			.map(toCommandCentreKpi);
	}, [data]);

	const financeMetrics = useMemo(() => {
		if (!data) return [];
		return FINANCE_METRIC_KEYS.map((key) => data.metrics.find((m) => m.key === key)).filter(
			(m): m is NonNullable<typeof m> => !!m
		);
	}, [data]);

	const basePath = isSuperAdmin ? "/super-admin" : "/hospital-admin";

	return (
		<div className="space-y-6 pb-10">
			<IntelligenceSubNav basePath={basePath} />

			<div>
				<div className="flex items-center gap-2 text-indigo-700">
					<IndianRupee className="h-6 w-6" />
					<h1 className="text-2xl font-bold tracking-tight text-slate-900">Finance Intelligence</h1>
				</div>
				<p className="text-sm text-muted-foreground mt-1">
					Revenue, collections, aging, expenses & cash flow · {data?.period.label ?? "—"}
				</p>
			</div>

			<CommandCentreFilters
				hospitals={hospitalOptions}
				showHospitalFilter={showHospitalFilter}
				hospitalId={selectedHospitalId}
				onHospitalChange={setSelectedHospitalId}
				period={period}
				onPeriodChange={setPeriod}
			/>

			{data && customReady && (
				<div className="flex justify-end gap-2">
					<IntelligenceExportButton
						filename={`finance-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
						buildRows={() => buildFinanceExportRows(data)}
						label="Quick CSV"
					/>
					<IntelligenceServerExportButton
						report="finance"
						params={queryParams}
						filename={`finance-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
					/>
				</div>
			)}

			{isLoading && customReady && (
				<div className="flex justify-center py-16 text-muted-foreground gap-2">
					<Loader2 className="h-5 w-5 animate-spin" /> Loading…
				</div>
			)}

			{isError && customReady && (
				<Card className="border-destructive/40">
					<CardContent className="pt-4 text-sm text-destructive flex gap-2">
						<AlertTriangle className="h-4 w-4 shrink-0" />
						{(error as Error)?.message ?? "Failed to load finance intelligence"}
					</CardContent>
				</Card>
			)}

			{data && customReady && (
				<>
					{data.booksOverlay ? (
						<Card className="border-slate-200 bg-slate-50/80">
							<CardHeader className="pb-2">
								<CardTitle className="text-base">
									Books overlay (double-entry)
									{data.booksOverlay.ready ? (
										<span className="ml-2 text-xs font-normal text-emerald-700">
											{data.booksOverlay.postedVoucherCount} posted vouchers
										</span>
									) : (
										<span className="ml-2 text-xs font-normal text-amber-700">
											No posted books in period yet
										</span>
									)}
								</CardTitle>
								<p className="text-xs text-muted-foreground font-normal">
									{data.booksOverlay.note}
								</p>
							</CardHeader>
							<CardContent className="space-y-3">
								{data.booksOverlay.variance.length > 0 ? (
									<div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>Metric</TableHead>
													<TableHead className="text-right">Ops</TableHead>
													<TableHead className="text-right">Books</TableHead>
													<TableHead className="text-right">Diff</TableHead>
													<TableHead className="text-right">%</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{data.booksOverlay.variance.map((v) => (
													<TableRow key={v.key}>
														<TableCell className="text-sm">{v.label}</TableCell>
														<TableCell className="text-right tabular-nums text-sm">
															{formatInr(v.ops)}
														</TableCell>
														<TableCell className="text-right tabular-nums text-sm">
															{formatInr(v.books)}
														</TableCell>
														<TableCell
															className={`text-right tabular-nums text-sm ${
																Math.abs(v.difference) > 0.5
																	? "text-amber-700"
																	: "text-slate-600"
															}`}
														>
															{formatInr(v.difference)}
														</TableCell>
														<TableCell className="text-right tabular-nums text-sm">
															{v.pctDiff != null ? `${v.pctDiff}%` : "—"}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								) : (
									<p className="text-sm text-muted-foreground">
										Seed CoA and post ops→books (or seed openings) to populate dual-run.
									</p>
								)}
							</CardContent>
						</Card>
					) : null}

					{data.statements.length > 0 && (
						<Card>
							<CardContent className="pt-4">
								<ul className="space-y-1 text-sm text-slate-700">
									{data.statements.map((s) => (
										<li key={s}>• {s}</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}

					<div>
						<h2 className="text-sm font-semibold text-slate-700 mb-3">Core financial KPIs</h2>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
							{coreKpis.map((kpi) => (
								<KpiCard
									key={kpi.key}
									kpi={kpi}
									comparisonLabel={data.comparisonPeriod?.label}
								/>
							))}
						</div>
					</div>

					<div>
						<h2 className="text-sm font-semibold text-slate-700 mb-3">Finance metrics</h2>
						<div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
							{financeMetrics.map((m) => (
								<FinanceMetricCard
									key={m.key}
									metric={m}
									comparisonLabel={data.comparisonPeriod?.label}
								/>
							))}
							{data.unavailable.map((u) => (
								<UnavailableFinanceCard key={u.key} metric={u} />
							))}
						</div>
					</div>

					<div className="grid lg:grid-cols-3 gap-4">
						<div className="lg:col-span-2">
							<FinanceTrendChart dailyTrend={data.dailyTrend} />
						</div>
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-base font-medium">Collection efficiency</CardTitle>
								<p className="text-xs text-muted-foreground">{data.collectionEfficiency.formula}</p>
							</CardHeader>
							<CardContent className="space-y-3 text-sm">
								<div>
									<p className="text-muted-foreground">Efficiency</p>
									<p className="text-2xl font-semibold tabular-nums">
										{data.collectionEfficiency.percent}%
									</p>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Collected on period revenue</span>
									<span className="font-medium tabular-nums">
										{formatInr(data.collectionEfficiency.collections)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Net revenue</span>
									<span className="font-medium tabular-nums">
										{formatInr(data.collectionEfficiency.netRevenue)}
									</span>
								</div>
								<p className="text-xs text-muted-foreground pt-1">
									Cash Inflow still counts all cash received in the period (including older dues).
								</p>
							</CardContent>
						</Card>
					</div>

					<RevenueCollectionMomPanel rows={data.revenueCollectionMom ?? []} />

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Receivables aging</CardTitle>
							<p className="text-xs text-muted-foreground">
								Snapshot as of {data.receivablesAging.snapshotAsOf} · Total outstanding{" "}
								{formatInr(data.receivablesAging.totalOutstanding)} · Overdue (&gt;30d){" "}
								{formatInr(data.receivablesAging.overdueOutstanding)}
							</p>
						</CardHeader>
						<CardContent className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Bucket</TableHead>
										<TableHead className="text-right">Amount</TableHead>
										<TableHead className="text-right">Share</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.receivablesAging.buckets.map((b) => (
										<TableRow key={b.key}>
											<TableCell>{b.label}</TableCell>
											<TableCell className="text-right tabular-nums">{formatInr(b.amount)}</TableCell>
											<TableCell className="text-right tabular-nums">{b.percent}%</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					<div className="grid md:grid-cols-3 gap-4">
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-base font-medium">Profitability (simplified)</CardTitle>
								<p className="text-xs text-muted-foreground font-normal">
									Operating expenses by service month; Capex and Funding excluded.
								</p>
							</CardHeader>
							<CardContent className="space-y-3 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Operating expense</span>
									<span className="font-medium tabular-nums">
										{formatInr(data.profitability.operatingExpense)}
									</span>
								</div>
								<div className="flex justify-between text-xs pl-2">
									<span className="text-muted-foreground">Patient expenses</span>
									<span className="tabular-nums">
										{formatInr(data.profitability.operatingExpensePatient)}
									</span>
								</div>
								<div className="flex justify-between text-xs pl-2">
									<span className="text-muted-foreground">Central expenses</span>
									<span className="tabular-nums">
										{formatInr(data.profitability.operatingExpenseCentral)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Operating profit</span>
									<span className="font-semibold tabular-nums">
										{formatInr(data.profitability.operatingProfit)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Operating margin</span>
									<span className="font-semibold tabular-nums">
										{data.profitability.operatingMarginPercent}%
									</span>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-base font-medium">One-time expenses</CardTitle>
								<p className="text-xs text-muted-foreground font-normal">
									By service month — not included in operating expense.
								</p>
							</CardHeader>
							<CardContent className="space-y-3 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Capex</span>
									<span className="font-medium tabular-nums">
										{formatInr(data.oneTimeExpenses?.capex ?? 0)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Funding expense</span>
									<span className="font-medium tabular-nums">
										{formatInr(data.oneTimeExpenses?.funding ?? 0)}
									</span>
								</div>
								<div className="flex justify-between border-t pt-2">
									<span className="text-muted-foreground">Total one-time</span>
									<span className="font-semibold tabular-nums">
										{formatInr(data.oneTimeExpenses?.total ?? 0)}
									</span>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-base font-medium">Cash flow (simplified)</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Cash inflow</span>
									<span className="font-medium tabular-nums text-emerald-700">
										{formatInr(data.cashFlow.cashInflow)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Cash outflow</span>
									<span className="font-medium tabular-nums text-rose-700">
										{formatInr(data.cashFlow.cashOutflow)}
									</span>
								</div>
								<div className="flex justify-between border-t pt-2">
									<span className="text-muted-foreground font-medium">Net cash flow</span>
									<span className="font-semibold tabular-nums">
										{formatInr(data.cashFlow.netCashFlow)}
									</span>
								</div>
							</CardContent>
						</Card>
					</div>

					{data.hospitalComparison.enabled && (
						<FinanceHospitalComparisonTable rows={data.hospitalComparison.rows} />
					)}

					<Card className="border-amber-200/60 bg-amber-50/30">
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Data limitations</CardTitle>
						</CardHeader>
						<CardContent>
							<ul className="space-y-1 text-xs text-muted-foreground">
								{data.limitations.map((l) => (
									<li key={l}>• {l}</li>
								))}
							</ul>
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}
